import InputHidden from 'components/Form/InputHidden';
import InputIncremental from 'components/Form/InputIncremental';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import Information from 'components/Information';
import Stacked from 'components/Layout/Stacked';
import LocaleImage from 'components/LocaleImage';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {c, t} from 'ttag';
import {FormFields} from 'types/utils';
import getPreferredLanguage from 'utils/locale';

import individualEN from '../assets/images/individual_en.png';
import individualJA from '../assets/images/individual_ja.png';
import PlanChangeFooter from '../footer';
import BillingChangeSubscriptionHeader from '../Header';
import parseChangeSubscriptionFormData from '../parse-change-subscription-form-data';
import PriceCalculation from '../PriceCalculation';
import {getTermOptionRadio} from '../selectableOptions';
import TabNavigation from '../TabNavigation';
import {
    BillingScreenName,
    ParsedSearchParams,
    Term,
    TermOptions,
} from '../types';
import useGetIndividualPrices from './services';

type PageIndividualFormData = FormFields<{
    billingFrequency: Term;
    plan_code: 'individual_2';
    teamMembers: number;
}>;

type PageIndividualProps = {
    isAuthenticated: boolean;
    onSuccess: (url: string) => void;
    onTabChange: (tag: BillingScreenName) => void;
    termOptions: TermOptions;
};

const PageIndividual: FC<PageIndividualProps> = ({
    isAuthenticated,
    onSuccess,
    onTabChange,
    termOptions,
}) => {
    const language = getPreferredLanguage();
    const methods = useForm<PageIndividualFormData>({
        defaultValues: {
            billingFrequency: 'monthly',
            teamMembers: 1,
        },
        shouldUnregister: true,
    });

    const {getValues, handleSubmit, watch} = methods;

    const teamMemberCount = getValues('teamMembers');

    const [searchParams, setSearchParams] = useState<ParsedSearchParams>(() =>
        parseChangeSubscriptionFormData(getValues()),
    );
    const {data, isError, isLoading, isSuccess} = useGetIndividualPrices({
        onError: () => {},
        searchParams,
    });

    watch((formData) => {
        setSearchParams(parseChangeSubscriptionFormData(formData));
    });

    const termOptionRadio = getTermOptionRadio(termOptions);
    const hasTermHelpText = termOptions.some((option) => option.disabled);

    const onSubmit = (formData: PageIndividualFormData) => {
        onSuccess(
            `/billing/plan/checkout/?${new URLSearchParams(
                parseChangeSubscriptionFormData(formData),
            ).toString()}`,
        );
    };

    return (
        <div>
            <BillingChangeSubscriptionHeader
                isAuthenticated={isAuthenticated}
            />
            <TabNavigation activeTab="individual" onTabChange={onTabChange} />
            <div className="grid w-max max-w-full grid-cols-2 bg-green-10">
                <div className="flex w-full flex-col items-center p-8">
                    <LocaleImage
                        className="max-h-48 max-w-48"
                        src={{
                            en: individualEN,
                            ja: individualJA,
                        }}
                    />
                    <div className="my-4 text-center">
                        {t`The MakeLeaps Individual Plan gives you up to 3 users, 10 clients and access to all our document types, starting at only ¥500/User.`}
                    </div>
                    <a
                        href={`https://www.makeleaps.com/${language}/pricing-detail`}
                    >
                        {t`Find out more`}
                    </a>
                </div>
                <div className="w-full">
                    <FormProvider {...methods}>
                        <form className="p-4" onSubmit={handleSubmit(onSubmit)}>
                            <InputHidden
                                name="plan_code"
                                value="individual_2"
                            />
                            <Stacked className="w-full">
                                <Stacked className="bg-green-50 p-4">
                                    <InputIncremental
                                        data-testid="teamMembers"
                                        helpText={t`¥500/Team Member/Month`}
                                        label={c('Billing').t`Team Members`}
                                        name="teamMembers"
                                        required={true}
                                        requiredType="asterisk"
                                    />
                                    {teamMemberCount > 3 && (
                                        <Information
                                            data-testid="teamMemberWarning"
                                            type="alert"
                                        >
                                            {t`Need more than 3 users? Upgrade to our MakeLeaps Organization Plan`}
                                        </Information>
                                    )}
                                </Stacked>
                                <Radio
                                    data-testid="billingFrequency"
                                    helpText={
                                        hasTermHelpText
                                            ? t`(This plan only supports monthly payment)`
                                            : undefined
                                    }
                                    label={t`Billing Frequency`}
                                    name="billingFrequency"
                                    options={termOptionRadio}
                                    required={true}
                                    requiredType="asterisk"
                                />
                                <div className="grid rounded bg-green-200">
                                    <PriceCalculation
                                        isError={isError}
                                        isInvalid={teamMemberCount > 3}
                                        isLoading={isLoading}
                                        isSuccess={isSuccess}
                                        value={data?.amount}
                                    />
                                    <SubmitButton
                                        disabled={
                                            teamMemberCount > 3 || isError
                                        }
                                    >
                                        {t`Subscribe`}
                                    </SubmitButton>
                                </div>
                            </Stacked>
                        </form>
                    </FormProvider>
                </div>
            </div>
            <PlanChangeFooter />
        </div>
    );
};

export default PageIndividual;
